/**
 * Family Service
 *
 * Family management API methods.
 */

import { TonnieClient } from '../client/base';
import { API_ENDPOINTS } from '../client/config';

export interface FamilyMember {
  id: string;
  userId: string;
  name: string;
  relationship: 'spouse' | 'son' | 'daughter' | 'parent' | 'sibling' | 'other';
  age: number;
  dateOfBirth?: string;
  status: 'included' | 'pending' | 'not_included';
  documentsComplete: boolean;
  visaStatus?: 'not_started' | 'in_progress' | 'approved' | 'denied';
  schoolEnrollment?: string;
  employmentStatus?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFamilyMemberInput {
  name: string;
  relationship: FamilyMember['relationship'];
  dateOfBirth?: string;
  age?: number;
}

export interface UpdateFamilyMemberInput {
  name?: string;
  relationship?: FamilyMember['relationship'];
  dateOfBirth?: string;
  age?: number;
  status?: FamilyMember['status'];
  schoolEnrollment?: string;
  employmentStatus?: string;
}

export interface FamilyDocument {
  id: string;
  memberId: string;
  type: 'birth_certificate' | 'passport' | 'visa' | 'vaccination' | 'school_records' | 'other';
  name: string;
  status: 'pending' | 'verified' | 'rejected';
  uploadedAt: string;
  fileUrl?: string;
}

export interface UploadFamilyDocumentInput {
  memberId: string;
  type: FamilyDocument['type'];
  file: File;
}

export interface FamilyInviteInput {
  email: string;
  relationship: FamilyMember['relationship'];
  name: string;
}

export class FamilyService {
  constructor(private readonly client: TonnieClient) {}

  /**
   * Get all family members
   */
  async getMembers(): Promise<FamilyMember[]> {
    return this.client.get<FamilyMember[]>(API_ENDPOINTS.FAMILY.MEMBERS);
  }

  /**
   * Get family member by ID
   */
  async getMember(id: string): Promise<FamilyMember> {
    return this.client.get<FamilyMember>(API_ENDPOINTS.FAMILY.BY_ID(id));
  }

  /**
   * Add a family member
   */
  async addMember(input: CreateFamilyMemberInput): Promise<FamilyMember> {
    return this.client.post<FamilyMember>(API_ENDPOINTS.FAMILY.MEMBERS, input);
  }

  /**
   * Update family member
   */
  async updateMember(id: string, input: UpdateFamilyMemberInput): Promise<FamilyMember> {
    return this.client.patch<FamilyMember>(API_ENDPOINTS.FAMILY.BY_ID(id), input);
  }

  /**
   * Remove family member
   */
  async removeMember(id: string): Promise<void> {
    return this.client.delete<void>(API_ENDPOINTS.FAMILY.BY_ID(id));
  }

  /**
   * Get documents for a family member
   */
  async getMemberDocuments(memberId: string): Promise<FamilyDocument[]> {
    return this.client.get<FamilyDocument[]>(API_ENDPOINTS.FAMILY.DOCUMENTS(memberId));
  }

  /**
   * Upload document for a family member
   */
  async uploadDocument(input: UploadFamilyDocumentInput): Promise<FamilyDocument> {
    const formData = new FormData();
    formData.append('type', input.type);
    formData.append('file', input.file);

    const response = await fetch(
      `${this.client['config'].baseUrl}${API_ENDPOINTS.FAMILY.DOCUMENTS(input.memberId)}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.client.getAuthToken()}`,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error('Failed to upload document');
    }

    return response.json();
  }

  /**
   * Invite a family member to join the platform
   */
  async inviteMember(input: FamilyInviteInput): Promise<{ inviteId: string; sent: boolean }> {
    return this.client.post<{ inviteId: string; sent: boolean }>(
      API_ENDPOINTS.FAMILY.INVITE,
      input
    );
  }
}

export function createFamilyService(client: TonnieClient): FamilyService {
  return new FamilyService(client);
}
